import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  TablePagination,
  TextField,
  IconButton,
  Badge,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Receipt as CheckIcon,
  ListAlt as ListAltIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import CheckDetailView from '../components/checks/CheckDetailView';
import ClientOrderItemCard from '../components/clientAccount/ClientOrderItemCard';
import CheckTransactionsTable from '../components/checks/CheckTransactionsTable';
import { BalanceSummaryCards } from '../components/checks/CheckBalanceCards';
import { useAuth } from '../context/AuthContext';
import { ALL_ORDER_STATUSES, CHECK_STATUSES } from '../constants/appConstants';
import { formatDateTime } from '../utils/dateFormat';
import axios from 'axios';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../utils/tablePagePreferences';
import SelectField from '../components/inputs/SelectField';
import {
  formatCheckListAmount,
  getCheckStatusLabel as getCheckStatusLabelI18n,
  getCheckStatusColor as getCheckStatusColorUtil,
} from '../utils/checkDisplay';
import { buildClientBalanceCardEntries } from '../utils/checkBalanceDisplay';
import { getItemStatusColorClientDetail, getItemStatusLabel as getOrderItemStatusLabel, getItemDisplayPrice } from '../utils/orderItemDisplay';
import { useTranslation } from 'react-i18next';

const MyAccount = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [detailTab, setDetailTab] = useState(0);

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState(25);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemStatusFilter, setItemStatusFilter] = useState('');
  const [itemDateFrom, setItemDateFrom] = useState('');
  const [itemDateTo, setItemDateTo] = useState('');

  const [myChecks, setMyChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checkDetail, setCheckDetail] = useState(null);
  const [checkDetailLoading, setCheckDetailLoading] = useState(false);

  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const openChecksBadgeCount = useMemo(
    () => myChecks.filter((c) => c.status === CHECK_STATUSES.OPEN || c.status === CHECK_STATUSES.PARTIALLY_PAID).length,
    [myChecks],
  );

  const displayName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'My Account';

  const fetchMyChecks = useCallback(async () => {
    setChecksLoading(true);
    try {
      const res = await axios.get('/api/checks/my');
      setMyChecks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch checks:', error);
      setMyChecks([]);
    } finally {
      setChecksLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async (page = 0, status = '', dateFrom = '', dateTo = '', limitOverride) => {
    setItemsLoading(true);
    try {
      const limit = limitOverride != null ? limitOverride : itemsRowsPerPage;
      const params = { page: page + 1, limit, noCheck: 'true' };
      if (status) params.status = status;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await axios.get('/api/order-items', { params });
      setItems(res.data.orderItems || []);
      setItemsTotal(res.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setItemsLoading(false);
    }
  }, [itemsRowsPerPage]);

  const fetchBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await axios.get('/api/payments/my/balance');
      setBalance(res.data.balance);
      setTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const fetchCheckDetail = async (checkId) => {
    setCheckDetailLoading(true);
    try {
      const res = await axios.get(`/api/checks/my/${checkId}`);
      setCheckDetail(res.data);
    } catch (error) {
      console.error('Failed to fetch check detail:', error);
    } finally {
      setCheckDetailLoading(false);
    }
  };

  const openCheckDetail = (check) => {
    setCheckDetail(null);
    fetchCheckDetail(check._id);
  };

  const closeCheckDetail = () => {
    setCheckDetail(null);
    fetchMyChecks();
    fetchItems(itemsPage, itemStatusFilter, itemDateFrom, itemDateTo);
  };

  useEffect(() => {
    fetchMyChecks();
  }, [fetchMyChecks]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let limit = 25;
      try {
        const { data } = await axios.get('/api/users/preferences');
        limit = pickRowsPerPageFromPrefs(data || {}, TABLE_ROWS_PREF_KEYS.myAccountItems, [10, 25, 50, 100], 25);
      } catch (_) {
        /* keep 25 */
      }
      if (cancelled) return;
      setItemsRowsPerPage(limit);
      await fetchItems(0, '', '', '', limit);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems]);

  const handleDetailTabChange = (event, newValue) => {
    setDetailTab(newValue);
    if (newValue !== 1) setCheckDetail(null);
    if (newValue === 0) {
      fetchItems(itemsPage, itemStatusFilter, itemDateFrom, itemDateTo);
    }
    if (newValue === 1) {
      fetchMyChecks();
    }
    if (newValue === 2) {
      fetchBalance();
    }
  };

  const getCheckStatusColor = (status) => {
    switch (status) {
      case CHECK_STATUSES.OPEN: return 'info';
      case CHECK_STATUSES.PARTIALLY_PAID: return 'warning';
      case CHECK_STATUSES.CLOSED: return 'success';
      default: return 'default';
    }
  };

  const getCheckStatusLabel = (status) => {
    const labels = {
      [CHECK_STATUSES.OPEN]: 'Open',
      [CHECK_STATUSES.PARTIALLY_PAID]: 'Partially Paid',
      [CHECK_STATUSES.CLOSED]: 'Closed',
    };
    return labels[status] || status || '—';
  };

  const balanceCards = buildClientBalanceCardEntries({
    totalItems: balance?.totalItems,
    totalPaid: balance?.totalPaid,
    totalDeposits: balance?.totalDeposits,
    amountDue: balance?.amountDue,
    checkCurrency: balance?.checkCurrency,
    defaultItemCurrency: balance?.defaultItemCurrency,
    showDualCurrency: balance?.showDualCurrency,
    totalInDefaultCurrency: balance?.totalItemsInDefaultCurrency,
    totalPaidInDefaultCurrency: balance?.totalPaidInDefaultCurrency,
    totalDepositsInDefaultCurrency: balance?.totalDepositsInDefaultCurrency,
    amountDueInDefaultCurrency: balance?.amountDueInDefaultCurrency,
  }, t);

  return (
    <Layout>
      <Container maxWidth="xl" disableGutters>
        {/* Header */}
        <Box mb={2}>
          <Typography variant="h5" fontWeight="bold">{displayName}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={detailTab} onChange={handleDetailTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab
              icon={<Badge badgeContent={itemsTotal} color="primary" max={999} invisible={itemsTotal === 0}><ListAltIcon fontSize="small" /></Badge>}
              iconPosition="start" label="Items"
            />
            <Tab
              icon={<Badge badgeContent={openChecksBadgeCount} color="warning" max={99} invisible={openChecksBadgeCount === 0}><CheckIcon fontSize="small" /></Badge>}
              iconPosition="start" label="Checks"
            />
            <Tab label="Balance" icon={<WalletIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Paper>

        {/* ── Items tab ── */}
        {detailTab === 0 && (
          <>
            <Paper sx={{ p: 1.5, mb: 2 }}>
              <Stack direction="column" spacing={1.5}>
                <SelectField
                  label="Status"
                  fullWidth
                  value={itemStatusFilter}
                  onChange={(next) => { setItemStatusFilter(next); setItemsPage(0); fetchItems(0, next, itemDateFrom, itemDateTo); }}
                  options={ALL_ORDER_STATUSES.map((s) => ({ value: s, label: getOrderItemStatusLabel(s, t) }))}
                  emptyLabel="All"
                  includeEmptyOption
                />
                <Stack direction="row" spacing={1}>
                  <TextField size="small" label="From" type="date" value={itemDateFrom} fullWidth
                    onChange={(e) => { setItemDateFrom(e.target.value); setItemsPage(0); fetchItems(0, itemStatusFilter, e.target.value, itemDateTo); }}
                    InputLabelProps={{ shrink: true }} />
                  <TextField size="small" label="To" type="date" value={itemDateTo} fullWidth
                    onChange={(e) => { setItemDateTo(e.target.value); setItemsPage(0); fetchItems(0, itemStatusFilter, itemDateFrom, e.target.value); }}
                    InputLabelProps={{ shrink: true }} />
                </Stack>
              </Stack>
            </Paper>

            {itemsLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
            ) : (
              <>
                {items.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>No items found.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {items.map((item) => (
                      <ClientOrderItemCard
                        key={item._id}
                        item={item}
                        t={t}
                        displayPrice={getItemDisplayPrice(item)}
                      />
                    ))}
                  </Stack>
                )}
                <TablePagination
                  component="div"
                  count={itemsTotal}
                  page={itemsPage}
                  rowsPerPage={itemsRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  onPageChange={(e, newPage) => { setItemsPage(newPage); fetchItems(newPage, itemStatusFilter, itemDateFrom, itemDateTo); }}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    setItemsRowsPerPage(next); setItemsPage(0);
                    saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.myAccountItems, next).catch(() => {});
                    fetchItems(0, itemStatusFilter, itemDateFrom, itemDateTo, next);
                  }}
                />
              </>
            )}
          </>
        )}

        {/* ── Checks tab ── */}
        {detailTab === 1 && (
          checkDetailLoading && !checkDetail ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : checkDetail ? (
            <>
              <CheckDetailView
                check={checkDetail.check}
                balance={checkDetail.balance}
                items={checkDetail.items}
                transactions={checkDetail.transactions}
                isMobile={isMobile}
                isClient
                isManager={false}
                isInternal={false}
                canEditExchangeRates={false}
                compactBalance
                headerSlot={(
                  <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                    <IconButton onClick={closeCheckDetail} size="small" sx={{ mt: 0.5, flexShrink: 0 }}>
                      <BackIcon />
                    </IconButton>
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <CheckIcon color="primary" fontSize="small" />
                        <Typography variant="h6" noWrap>
                          {checkDetail.check.name || t('cashiering.checkNumber', { id: checkDetail.check._id.slice(-6) })}
                        </Typography>
                        <Chip
                          label={getCheckStatusLabelI18n(checkDetail.check.status, t)}
                          color={getCheckStatusColorUtil(checkDetail.check.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {checkDetail.check.createdAt ? formatDateTime(checkDetail.check.createdAt) : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                getItemStatusColor={getItemStatusColorClientDetail}
                getItemStatusChipLabel={(item) => getOrderItemStatusLabel(item.status, t)}
                itemsSectionTitle={t('cashiering.items')}
                noItemsMessage={t('cashiering.noItemsFound')}
                transactionsSectionTitle={t('cashiering.paymentsTransactions')}
                emptyTransactionsMessage={t('cashiering.noTransactions')}
                desktopTransactionsEmptyMode="standalone"
              />
            </>
          ) : checksLoading && myChecks.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
          ) : (
            /* Checks list */
            myChecks.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>No checks yet.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {myChecks.map((check) => (
                  <Card key={check._id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => openCheckDetail(check)}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="body1" fontWeight="bold">
                          {check.name || `Check #${check._id.slice(-6)}`}
                        </Typography>
                        <Chip label={getCheckStatusLabel(check.status)} color={getCheckStatusColor(check.status)} size="small" />
                      </Box>
                      <Divider sx={{ mb: 1 }} />
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Items</Typography>
                          <Typography variant="body2">{check.itemCount || 0}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                          <Typography variant="body2">{formatCheckListAmount(check.totalItems, check)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Payments</Typography>
                          <Typography variant="body2">{formatCheckListAmount(check.totalDeposits, check)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Amount Due</Typography>
                          <Typography variant="body2" fontWeight="bold" color={(check.amountDue || 0) > 0 ? 'error.main' : 'success.main'}>
                            {formatCheckListAmount(check.amountDue, check)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        {check.createdAt ? formatDateTime(check.createdAt) : '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )
          )
        )}

        {/* ── Balance tab ── */}
        {detailTab === 2 && (
          balanceLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
          ) : (
            <>
              <BalanceSummaryCards
                cards={balanceCards}
                isMobile={isMobile}
                compact
              />
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                {t('clients.transactionHistory')}
              </Typography>
              <CheckTransactionsTable
                transactions={transactions}
                paymentCurrency={balance?.checkCurrency}
                isMobile={isMobile}
                isManager={false}
                emptyMessage={t('clients.noTransactions')}
                desktopEmptyMode="standalone"
                emptyStandaloneSx={{ py: 3 }}
              />
            </>
          )
        )}
      </Container>
    </Layout>
  );
};

export default MyAccount;
